/**
 * Compact Active / Inactive control. Green check = Active, muted X = Inactive.
 * Used on KPI catalog (table + cards) and other status toggles.
 */

import React from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';

type ActiveStatusIconProps = {
  status?: string;
  onToggle?: () => void;
  disabled?: boolean;
};

export const ActiveStatusIcon: React.FC<ActiveStatusIconProps> = ({
  status,
  onToggle,
  disabled = false
}) => {
  const { t } = useLanguage();
  const isActive = (status || '').toUpperCase() === 'ACTIVE';
  const label = isActive
    ? t('kpi.filterStatusActive') || 'Active'
    : t('kpi.filterStatusInactive') || 'Inactive';
  const action = isActive
    ? t('common.deactivate') || 'Deactivate'
    : t('common.activate') || 'Activate';

  const icon = isActive ? (
    <CheckCircleIcon className="h-5 w-5 text-[var(--color-success)]" aria-hidden />
  ) : (
    <XCircleIcon className="h-5 w-5 text-[var(--color-secondary-text)] opacity-70" aria-hidden />
  );

  if (!onToggle) {
    return (
      <span className="inline-flex items-center" title={label} aria-label={label}>
        {icon}
      </span>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        onToggle();
      }}
      className="inline-flex items-center rounded-md p-0.5 hover:bg-[var(--color-secondary-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-50"
      title={`${label} — ${action}`}
      aria-label={action}
    >
      {icon}
    </button>
  );
};
